import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight } from 'lucide-react';
import { getCart, updateCartQuantity, removeFromCart, getCartTotal, getCurrency, convertPrice, getCurrencySymbol } from '@/lib/cartStore';

export default function Cart() {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [currency, setCurrency] = useState(getCurrency());

  const refresh = () => { setCart(getCart()); setCurrency(getCurrency()); };
  useEffect(() => {
    refresh();
    window.addEventListener('cart-updated', refresh);
    window.addEventListener('currency-updated', refresh);
    return () => { window.removeEventListener('cart-updated', refresh); window.removeEventListener('currency-updated', refresh); };
  }, []);

  const total = getCartTotal();

  if (cart.length === 0) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-muted flex items-center justify-center mb-4"><ShoppingBag className="h-8 w-8 text-muted-foreground" /></div>
        <h1 className="font-heading font-bold text-2xl text-foreground mb-2">Tu carrito está vacío</h1>
        <p className="text-muted-foreground text-sm mb-6">Explora nuestros productos y añade tus favoritos.</p>
        <Link to="/productos"><Button className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full">Explorar Productos</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-6">Mi Carrito</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-3" data-testid="cart-items">
          {cart.map((item) => (
            <div key={item.product_id} className="flex items-center gap-4 bg-card rounded-xl border border-border p-3">
              <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted shrink-0">{item.image_url ? <img src={item.image_url} alt={item.product_name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-2xl">🛒</div>}</div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-foreground truncate">{item.product_name}</p>
                <p className="text-sm text-[#1a3a5c] font-bold mt-0.5">{getCurrencySymbol(currency)}{convertPrice(item.price, currency)}</p>
              </div>
              <div className="flex items-center border border-border rounded-full">
                <button onClick={() => updateCartQuantity(item.product_id, item.quantity - 1)} className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded-l-full"><Minus className="h-3.5 w-3.5" /></button>
                <span className="w-8 text-center text-sm font-semibold">{item.quantity}</span>
                <button onClick={() => updateCartQuantity(item.product_id, item.quantity + 1)} className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded-r-full"><Plus className="h-3.5 w-3.5" /></button>
              </div>
              <p className="font-semibold text-sm w-20 text-right">{getCurrencySymbol(currency)}{convertPrice(item.price * item.quantity, currency)}</p>
              <button onClick={() => removeFromCart(item.product_id)} data-testid={`remove-${item.product_id}`} className="text-muted-foreground hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="bg-card rounded-xl border border-border p-5 h-fit sticky top-28">
          <h2 className="font-heading font-semibold text-base mb-4">Resumen</h2>
          <div className="flex justify-between text-sm mb-2"><span className="text-muted-foreground">Subtotal</span><span className="font-medium">{getCurrencySymbol(currency)}{convertPrice(total, currency)}</span></div>
          <p className="text-xs text-muted-foreground mb-4" data-testid="cart-estimate-note">Importes guardados como referencia. Precios, disponibilidad y envío se vuelven a calcular en el checkout en USD; otras monedas son conversiones orientativas.</p>
          <Button onClick={() => navigate('/checkout')} data-testid="checkout-button" className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full font-semibold h-11 gap-2">Finalizar Pedido <ArrowRight className="h-4 w-4" /></Button>
          <Link to="/productos" className="block text-center text-sm text-muted-foreground hover:text-secondary mt-3">Seguir comprando</Link>
        </div>
      </div>
    </div>
  );
}
